import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Slider,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getData } from "../../Webservice/Webservice";

const rubikfont = {
  fontFamily: "Rubik, sans-serif",
};

const DEFAULT_CONTAINER_TYPES = ["bottle", "box", "packet", "can", "jar", "bag"];
const DEFAULT_UNIT_TYPES = ["unit", "weight"];

const Filter = ({
  open,
  onClose,
  priceRange,
  setPriceRange,
  selectedBrands,
  setSelectedBrands,
  selectedIdealFor,
  setSelectedIdealFor,
  selectedContainerType = [],
  setSelectedContainerType = () => { },
  selectedUnitType = [],
  setSelectedUnitType = () => { },
  codAvailable = "all",
  setCodAvailable = () => { },
  brands: propsBrands = [],
  colors,
  containerTypes: propsContainerTypes = [],
  unitTypes: propsUnitTypes = [],
  onClearFilters,
  onApply,
}) => {
  const [filterData, setFilterData] = useState(null);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await getData('product/filters', {}, token, false);
        if (response && response.success && response.data) {
          setFilterData(response.data);

          // Optionally update parent price range if it's default
          if (priceRange[0] === 0 && priceRange[1] === 5000) {
            setPriceRange([
              Math.floor(response.data.priceRange?.min || 0),
              Math.ceil(response.data.priceRange?.max || 5000)
            ]);
          }
        }
      } catch (error) {
        console.error("Error fetching filters:", error);
      }
    };
    if (open) {
      fetchFilters();
    }
  }, [open]);

  const brands = filterData?.brands || propsBrands;
  const containerTypes = filterData?.container_type || propsContainerTypes || DEFAULT_CONTAINER_TYPES;
  const unitTypes = filterData?.unitType || propsUnitTypes || DEFAULT_UNIT_TYPES;
  const idealForOptions = filterData?.ideal_for ? ["all", ...filterData.ideal_for] : ["all", "men", "unisex"];

  const handleBrandChange = (brand) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };

  const handleContainerTypeChange = (type) => {
    setSelectedContainerType((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleUnitTypeChange = (type) => {
    setSelectedUnitType((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handlePriceChange = (event, newValue) => {
    setPriceRange(newValue);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: 420 },
          maxWidth: "100vw",
          boxSizing: "border-box",
          boxShadow: "0 0 24px rgba(0,0,0,0.1)",
          border: "none",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 3,
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "sticky",
          top: 0,
          backgroundColor: "background.paper",
          zIndex: 1,
        }}
      >
        <Typography
          variant="h5"
          sx={{ ...rubikfont, fontWeight: 700, color: "text.primary" }}
        >
          Filters
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            backgroundColor: "action.hover",
            "&:hover": {
              backgroundColor: "action.selected",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          p: 3,
          overflowY: "auto",
          height: "calc(100vh - 140px)",
          "&::-webkit-scrollbar": {
            width: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "divider",
            borderRadius: 2,
          },
        }}
      >
        {/* Price Filter */}
        <Box
          sx={{
            mb: 2,
            p: 2.5,
            borderRadius: 2,
            backgroundColor: "background.default",
          }}
        >
          <FormLabel
            component="legend"
            sx={{
              ...rubikfont,
              fontWeight: 600,
              mb: 2.5,
              display: "block",
              fontSize: "1rem",
              color: "text.primary",
            }}
          >
            Price Range
          </FormLabel>
          <Slider
            value={priceRange}
            onChange={handlePriceChange}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `₹${value}`}
            min={Math.floor(filterData?.priceRange?.min || 0)}
            max={Math.ceil(filterData?.priceRange?.max || 5000)}
            sx={{
              mb: 2,
              color: "#222",
              "& .MuiSlider-thumb": {
                width: 16,
                height: 16,
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "background.paper",
                px: 2,
                py: 1,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                ₹{priceRange[0]}
              </Typography>
            </Box>
            <Box sx={{ width: 8, height: 1, backgroundColor: "divider", mx: 1 }} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "background.paper",
                px: 2,
                py: 1,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                ₹{priceRange[1]}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Brand Filter */}
        <Box
          sx={{
            mb: 2,
            p: 2.5,
            borderRadius: 2,
            backgroundColor: "background.default",
          }}
        >
          <FormLabel
            component="legend"
            sx={{
              ...rubikfont,
              fontWeight: 600,
              mb: 2,
              display: "block",
              fontSize: "1rem",
              color: "text.primary",
            }}
          >
            Brand
          </FormLabel>
          <FormGroup sx={{ gap: 0.5 }}>
            {brands.map((brand) => (
              <FormControlLabel
                key={brand}
                control={
                  <Checkbox
                    checked={selectedBrands.includes(brand)}
                    onChange={() => handleBrandChange(brand)}
                    sx={{
                      color: "action.active",
                      "&.Mui-checked": {
                        color: "#222",
                      },
                      padding: "8px",
                    }}
                  />
                }
                label={
                  <Typography sx={{ ...rubikfont, fontSize: "0.95rem" }}>
                    {brand}
                  </Typography>
                }
                sx={{
                  margin: 0,
                  padding: "6px 8px",
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              />
            ))}
          </FormGroup>
        </Box>

        {/* Ideal For Filter */}
        <Box
          sx={{
            mb: 2,
            p: 2.5,
            borderRadius: 2,
            backgroundColor: "background.default",
          }}
        >
          <FormLabel
            component="legend"
            sx={{
              ...rubikfont,
              fontWeight: 600,
              mb: 2,
              display: "block",
              fontSize: "1rem",
              color: "text.primary",
            }}
          >
            Ideal For
          </FormLabel>
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <RadioGroup
              value={selectedIdealFor}
              onChange={(e) => setSelectedIdealFor(e.target.value)}
              sx={{ gap: 0.5 }}
            >
              {idealForOptions.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={
                    <Radio
                      sx={{
                        color: "action.active",
                        "&.Mui-checked": {
                          color: "#222",
                        },
                        padding: "8px",
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        ...rubikfont,
                        fontSize: "0.95rem",
                        textTransform: "capitalize",
                      }}
                    >
                      {option === "all" ? "All" : option}
                    </Typography>
                  }
                  sx={{
                    margin: 0,
                    padding: "6px 8px",
                    borderRadius: 1,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Container Type Filter */}
        <Box
          sx={{
            mb: 2,
            p: 2.5,
            borderRadius: 2,
            backgroundColor: "background.default",
          }}
        >
          <FormLabel
            component="legend"
            sx={{
              ...rubikfont,
              fontWeight: 600,
              mb: 2,
              display: "block",
              fontSize: "1rem",
              color: "text.primary",
            }}
          >
            Container Type
          </FormLabel>
          <FormGroup sx={{ gap: 0.5 }}>
            {(containerTypes || DEFAULT_CONTAINER_TYPES).map((type) => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    checked={(selectedContainerType || []).includes(type)}
                    onChange={() => handleContainerTypeChange(type)}
                    sx={{
                      color: "action.active",
                      "&.Mui-checked": {
                        color: "#222",
                      },
                      padding: "8px",
                    }}
                  />
                }
                label={
                  <Typography sx={{ ...rubikfont, fontSize: "0.95rem" }}>
                    {type}
                  </Typography>
                }
                sx={{
                  margin: 0,
                  padding: "6px 8px",
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              />
            ))}
          </FormGroup>
        </Box>

        {/* Unit Type Filter */}
        <Box
          sx={{
            mb: 2,
            p: 2.5,
            borderRadius: 2,
            backgroundColor: "background.default",
          }}
        >
          <FormLabel
            component="legend"
            sx={{
              ...rubikfont,
              fontWeight: 600,
              mb: 2,
              display: "block",
              fontSize: "1rem",
              color: "text.primary",
            }}
          >
            Unit Type
          </FormLabel>
          <FormGroup sx={{ gap: 0.5 }}>
            {(unitTypes || DEFAULT_UNIT_TYPES).map((type) => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    checked={(selectedUnitType || []).includes(type)}
                    onChange={() => handleUnitTypeChange(type)}
                    sx={{
                      color: "action.active",
                      "&.Mui-checked": {
                        color: "#222",
                      },
                      padding: "8px",
                    }}
                  />
                }
                label={
                  <Typography sx={{ ...rubikfont, fontSize: "0.95rem" }}>
                    {type}
                  </Typography>
                }
                sx={{
                  margin: 0,
                  padding: "6px 8px",
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              />
            ))}
          </FormGroup>
        </Box>

        {/* COD Available Filter */}
        <Box
          sx={{
            mb: 2,
            p: 2.5,
            borderRadius: 2,
            backgroundColor: "background.default",
          }}
        >
          <FormLabel
            component="legend"
            sx={{
              ...rubikfont,
              fontWeight: 600,
              mb: 2,
              display: "block",
              fontSize: "1rem",
              color: "text.primary",
            }}
          >
            COD Available
          </FormLabel>
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <RadioGroup
              value={codAvailable}
              onChange={(e) => setCodAvailable(e.target.value)}
              sx={{ gap: 0.5 }}
            >
              {["all", "yes", "no"].map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={
                    <Radio
                      sx={{
                        color: "action.active",
                        "&.Mui-checked": {
                          color: "#222",
                        },
                        padding: "8px",
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        ...rubikfont,
                        fontSize: "0.95rem",
                        textTransform: "capitalize",
                      }}
                    >
                      {option === "all" ? "All" : option}
                    </Typography>
                  }
                  sx={{
                    margin: 0,
                    padding: "6px 8px",
                    borderRadius: 1,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>


      </Box>

      {/* Sticky Action Buttons */}
      <Box
        sx={{
          p: 3,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          position: "sticky",
          bottom: 0,
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onClearFilters}
            sx={{
              ...rubikfont,
              py: 1.5,
              borderColor: "divider",
              color: "text.primary",
              fontWeight: 500,
              "&:hover": {
                borderColor: "text.primary",
                backgroundColor: "transparent",
              },
            }}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              if (onApply) onApply();
              onClose();
            }}
            sx={{
              ...rubikfont,
              py: 1.5,
              backgroundColor: "#222",
              fontWeight: 600,
              fontSize: "0.95rem",
              "&:hover": {
                backgroundColor: "#444",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              },
              "&:active": {
                transform: "translateY(0)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Filter;